import { CatalogueItemCategory, Currency } from './Catalogue';

/**
 * Types of filter inputs supported by the filter bar
 */
export enum CatalogueFilterType {
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  SELECT = 'select',
  AUTOCOMPLETE = 'autocomplete',
  BOOLEAN = 'boolean',
}

/**
 * Unique identifiers for each filter
 */
export enum CatalogueFilterKey {
  // Core fields
  SEARCH = 'search',
  ERP_SEARCH = 'erpSearch',
  DESCRIPTION = 'description',
  CATEGORY = 'category',
  GROUP = 'groupUuid',
  ASSEMBLIES_ONLY = 'assembliesOnly',

  // Cost & versioning
  COST = 'cost',
  TOTAL_COST = 'totalCost',
  CURRENCY = 'currency',
  VERSION = 'version',
  CREATED_AT = 'createdAt',

  // Technical specifications
  ACTIVATION_PRINCIPLE = 'activationPrinciple',
  GRIP_CONTROL_SENSOR = 'gripControlSensor',
  DISTANCE_SENSOR = 'distanceSensor',
  WEIGHT_G = 'weightG',
  STROKE_MM = 'strokeMm',
  MAX_GRIP_FORCE_N = 'maxGripForceN',
  IP_CLASS = 'ipClass',
  MATERIAL_TYPE = 'materialType',
  AIR_SUPPLY_NEEDED = 'airSupplyNeeded',
  PAYLOAD_KG = 'payloadKg',
  REACH_MM = 'reachMm',
  DOF = 'dof',
  VACUUM_SUPPLY_NEEDED = 'vacuumSupplyNeeded',
  PNEUMATIC_TUBE_DIAMETER_MM = 'pneumaticTubeDiameterMm',
  ROBOT_VERSION = 'robotVersion',

  // Inventory
  STOCK_STATUS = 'stockStatus',
  LOCATION = 'location',
}

/**
 * Operators supported by the backend filter system
 */
export enum CatalogueFilterOp {
  EQ = 'eq',
  NEQ = 'neq',
  CONTAINS = 'contains',
  STARTS_WITH = 'starts_with',
  IN = 'in',
  BETWEEN = 'between',
  GT = 'gt',
  GTE = 'gte',
  LT = 'lt',
  LTE = 'lte',
  DESCENDANTS = 'descendants',
}

/** Human-readable labels for operators */
export const CATALOGUE_FILTER_OP_LABELS: Record<CatalogueFilterOp, string> = {
  [CatalogueFilterOp.EQ]: '=',
  [CatalogueFilterOp.NEQ]: '≠',
  [CatalogueFilterOp.CONTAINS]: '∋',
  [CatalogueFilterOp.STARTS_WITH]: 'A..',
  [CatalogueFilterOp.IN]: 'in',
  [CatalogueFilterOp.BETWEEN]: '⟷',
  [CatalogueFilterOp.GT]: '>',
  [CatalogueFilterOp.GTE]: '≥',
  [CatalogueFilterOp.LT]: '<',
  [CatalogueFilterOp.LTE]: '≤',
  [CatalogueFilterOp.DESCENDANTS]: '⊇',
};

/** Available operators grouped by filter type */
export const TEXT_OPS = [
  CatalogueFilterOp.CONTAINS,
  CatalogueFilterOp.EQ,
  CatalogueFilterOp.NEQ,
  CatalogueFilterOp.STARTS_WITH,
];

export const NUMBER_OPS = [
  CatalogueFilterOp.EQ,
  CatalogueFilterOp.NEQ,
  CatalogueFilterOp.GT,
  CatalogueFilterOp.GTE,
  CatalogueFilterOp.LT,
  CatalogueFilterOp.LTE,
];

export const DATE_OPS = [
  CatalogueFilterOp.GTE,
  CatalogueFilterOp.LTE,
  CatalogueFilterOp.EQ,
  CatalogueFilterOp.GT,
  CatalogueFilterOp.LT,
];

export const ENUM_OPS = [CatalogueFilterOp.EQ, CatalogueFilterOp.NEQ];

export const BOOLEAN_OPS = [CatalogueFilterOp.EQ];

export const UUID_OPS = [CatalogueFilterOp.DESCENDANTS, CatalogueFilterOp.EQ];

/**
 * A single filter clause sent to the backend
 */
export interface CatalogueFilterClause {
  field: string;
  op: CatalogueFilterOp;
  value: string | number | boolean | (string | number)[];
}

/**
 * A single sort clause sent to the backend
 */
export interface CatalogueSortClause {
  field: string;
  order: 'asc' | 'desc';
}

/**
 * Query parameters for getCatalogueItems API call
 */
export interface CatalogueQueryParams {
  page?: number;
  limit?: number;
  filters?: CatalogueFilterClause[];
  sort?: CatalogueSortClause[];
}

/**
 * Filter group labels for organizing the filter dropdown
 */
export const CatalogueFilterGroup = {
  GENERAL: 'General',
  COST: 'Cost',
  TECHNICAL_SPECS: 'Technical Specs',
  INVENTORY: 'Inventory',
  CUSTOM_FIELDS: 'Custom Fields',
} as const;

export type CatalogueFilterGroupValue =
  (typeof CatalogueFilterGroup)[keyof typeof CatalogueFilterGroup];

/**
 * Definition for a filter that can be added to the filter bar
 */
export interface CatalogueFilterDefinition {
  key: string;
  label: string;
  type: CatalogueFilterType;
  icon: string;
  placeholder?: string;
  options?: string[];
  /** Backend field name for the filter clause */
  field: string;
  /** Default operator used when building the filter clause */
  defaultOp: CatalogueFilterOp;
  /** Available operators the user can switch between */
  availableOps: CatalogueFilterOp[];
  /** Group for organizing filters in the dropdown */
  group?: CatalogueFilterGroupValue;
}

/**
 * Registry of all available catalogue filters
 */
export const CATALOGUE_FILTER_REGISTRY: CatalogueFilterDefinition[] = [
  // --- General ---
  {
    key: CatalogueFilterKey.SEARCH,
    label: 'Name',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-magnify',
    placeholder: 'Search by name...',
    field: 'name',
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },
  {
    key: CatalogueFilterKey.ERP_SEARCH,
    label: 'Xentral ID',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-numeric',
    placeholder: 'Search by Xentral ID...',
    field: 'erp_id',
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },
  {
    key: CatalogueFilterKey.DESCRIPTION,
    label: 'Description',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-text',
    placeholder: 'Search description...',
    field: 'description',
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },
  {
    key: CatalogueFilterKey.CATEGORY,
    label: 'Category',
    type: CatalogueFilterType.SELECT,
    icon: 'mdi-tag',
    options: Object.values(CatalogueItemCategory),
    field: 'category',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: ENUM_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },
  {
    key: CatalogueFilterKey.GROUP,
    label: 'Group',
    type: CatalogueFilterType.AUTOCOMPLETE,
    icon: 'mdi-folder',
    placeholder: 'Select group...',
    field: 'group_uuid',
    defaultOp: CatalogueFilterOp.DESCENDANTS,
    availableOps: UUID_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },
  {
    key: CatalogueFilterKey.ASSEMBLIES_ONLY,
    label: 'Assemblies Only',
    type: CatalogueFilterType.BOOLEAN,
    icon: 'mdi-package-variant',
    field: 'assemblies_only',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: BOOLEAN_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },
  {
    key: CatalogueFilterKey.VERSION,
    label: 'Version',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-counter',
    placeholder: '1',
    field: 'version',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },
  {
    key: CatalogueFilterKey.CREATED_AT,
    label: 'Created At',
    type: CatalogueFilterType.DATE,
    icon: 'mdi-calendar',
    field: 'created_at',
    defaultOp: CatalogueFilterOp.GTE,
    availableOps: DATE_OPS,
    group: CatalogueFilterGroup.GENERAL,
  },

  // --- Cost ---
  {
    key: CatalogueFilterKey.COST,
    label: 'Cost',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-currency-eur',
    placeholder: '0.00',
    field: 'cost',
    defaultOp: CatalogueFilterOp.GTE,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.COST,
  },
  {
    key: CatalogueFilterKey.TOTAL_COST,
    label: 'Total Cost',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-cash-multiple',
    placeholder: '0.00',
    field: 'total_cost',
    defaultOp: CatalogueFilterOp.GTE,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.COST,
  },
  {
    key: CatalogueFilterKey.CURRENCY,
    label: 'Currency',
    type: CatalogueFilterType.SELECT,
    icon: 'mdi-cash',
    options: Object.values(Currency),
    field: 'currency',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: ENUM_OPS,
    group: CatalogueFilterGroup.COST,
  },

  // --- Technical specifications ---
  {
    key: CatalogueFilterKey.ACTIVATION_PRINCIPLE,
    label: 'Activation Principle',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-lightning-bolt',
    placeholder: 'e.g. pneumatic',
    field: 'activation_principle',
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.GRIP_CONTROL_SENSOR,
    label: 'Grip Control Sensor',
    type: CatalogueFilterType.BOOLEAN,
    icon: 'mdi-hand-back-right',
    field: 'grip_control_sensor',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: BOOLEAN_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.DISTANCE_SENSOR,
    label: 'Distance Sensor',
    type: CatalogueFilterType.BOOLEAN,
    icon: 'mdi-signal-distance-variant',
    field: 'distance_sensor',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: BOOLEAN_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.WEIGHT_G,
    label: 'Weight (g)',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-weight',
    placeholder: '0',
    field: 'weight_g',
    defaultOp: CatalogueFilterOp.LTE,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.STROKE_MM,
    label: 'Stroke (mm)',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-arrow-expand-horizontal',
    placeholder: '0',
    field: 'stroke_mm',
    defaultOp: CatalogueFilterOp.GTE,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.MAX_GRIP_FORCE_N,
    label: 'Max Grip Force (N)',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-hand-clap',
    placeholder: '0',
    field: 'max_grip_force_n',
    defaultOp: CatalogueFilterOp.GTE,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.IP_CLASS,
    label: 'IP Class',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-shield-check',
    placeholder: 'e.g. IP67',
    field: 'ip_class',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.MATERIAL_TYPE,
    label: 'Material Type',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-cube-outline',
    placeholder: 'e.g. aluminum',
    field: 'material_type',
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.AIR_SUPPLY_NEEDED,
    label: 'Air Supply Needed',
    type: CatalogueFilterType.BOOLEAN,
    icon: 'mdi-weather-windy',
    field: 'air_supply_needed',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: BOOLEAN_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.PAYLOAD_KG,
    label: 'Payload (kg)',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-weight-kilogram',
    placeholder: '0',
    field: 'payload_kg',
    defaultOp: CatalogueFilterOp.GTE,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.REACH_MM,
    label: 'Reach (mm)',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-arrow-expand-all',
    placeholder: '0',
    field: 'reach_mm',
    defaultOp: CatalogueFilterOp.GTE,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.DOF,
    label: 'Degrees of Freedom',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-axis-arrow',
    placeholder: '0',
    field: 'dof',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.VACUUM_SUPPLY_NEEDED,
    label: 'Vacuum Supply Needed',
    type: CatalogueFilterType.BOOLEAN,
    icon: 'mdi-vacuum',
    field: 'vacuum_supply_needed',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: BOOLEAN_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.PNEUMATIC_TUBE_DIAMETER_MM,
    label: 'Tube Diameter (mm)',
    type: CatalogueFilterType.NUMBER,
    icon: 'mdi-pipe',
    placeholder: '0',
    field: 'pneumatic_tube_diameter_mm',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: NUMBER_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },
  {
    key: CatalogueFilterKey.ROBOT_VERSION,
    label: 'Robot Version',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-robot',
    placeholder: 'e.g. v2',
    field: 'robot_version',
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.TECHNICAL_SPECS,
  },

  // --- Inventory ---
  {
    key: CatalogueFilterKey.STOCK_STATUS,
    label: 'Stock Status',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-warehouse',
    placeholder: 'e.g. in_stock',
    field: 'stock_status',
    defaultOp: CatalogueFilterOp.EQ,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.INVENTORY,
  },
  {
    key: CatalogueFilterKey.LOCATION,
    label: 'Location',
    type: CatalogueFilterType.TEXT,
    icon: 'mdi-map-marker',
    placeholder: 'Search location...',
    field: 'location',
    defaultOp: CatalogueFilterOp.CONTAINS,
    availableOps: TEXT_OPS,
    group: CatalogueFilterGroup.INVENTORY,
  },
];

export const getFilterDefinition = (
  key: string,
): CatalogueFilterDefinition | undefined => {
  return CATALOGUE_FILTER_REGISTRY.find((f) => f.key === key);
};

export const getFilters = (): CatalogueFilterDefinition[] => {
  return CATALOGUE_FILTER_REGISTRY;
};

export type CatalogueFilterValue = string | number | boolean | null;

export type CatalogueActiveFilters = Map<string, CatalogueFilterValue>;

/**
 * Sort field identifiers matching the backend allowed values
 */
export enum CatalogueSortField {
  NAME = 'name',
  ERP_ID = 'erp_id',
  CREATED_AT = 'created_at',
  COST = 'cost',
  TOTAL_COST = 'total_cost',
  CATEGORY = 'category',
  GROUP = 'group_uuid',
  VERSION = 'version',
}

export type CatalogueSortOrder = 'asc' | 'desc';

export interface CatalogueSortOption {
  value: CatalogueSortField;
  label: string;
}

export const CATALOGUE_SORT_OPTIONS: CatalogueSortOption[] = [
  { value: CatalogueSortField.NAME, label: 'Name' },
  { value: CatalogueSortField.ERP_ID, label: 'Xentral ID' },
  { value: CatalogueSortField.CREATED_AT, label: 'Created at' },
  { value: CatalogueSortField.COST, label: 'Cost' },
  { value: CatalogueSortField.TOTAL_COST, label: 'Total cost' },
  { value: CatalogueSortField.CATEGORY, label: 'Category' },
  { value: CatalogueSortField.GROUP, label: 'Group' },
  { value: CatalogueSortField.VERSION, label: 'Version' },
];
